// ======================================================================
// File: src/modules/ppe/pages/procurement/purchase-return/list/PurchaseReturnListPage.jsx
// Module: PPE → Procurement → Purchase Return
//
// Purpose:
// - Main Purchase Return listing screen
// - Follows same layout pattern as GRNListPage.jsx
// - Shows KPI bar, filter bar, table and pagination
// ======================================================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../purchase-return-ui.css";

import usePurchaseReturnList from "../hooks/usePurchaseReturnList";

import PurchaseReturnFilterBar from "./PurchaseReturnFilterBar";
import purchaseReturnColumns from "./purchaseReturnColumns";
import PurchaseReturnKpiBar from "./PurchaseReturnKpiBar";

import PurchaseReturnFormDrawer from "../form/PurchaseReturnFormDrawer";

export default function PurchaseReturnListPage() {

  const navigate = useNavigate();

  // --------------------------------------------------
  // LIST DATA USING CUSTOM HOOK
  // --------------------------------------------------
  const {
    items,
    total,
    loading,
    page,
    pageSize,
    setPage,
    filters,
    setFilters,
    reload,
  } = usePurchaseReturnList();

  // --------------------------------------------------
  // DRAWER STATE
  // --------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedPurchaseReturn, setSelectedPurchaseReturn] = useState(null);

  const canCreate = true;

  // --------------------------------------------------
  // DRAWER HANDLERS
  // --------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedPurchaseReturn(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedPurchaseReturn(null);
  };

  // --------------------------------------------------
  // UI RENDER
  // --------------------------------------------------
  return (
    <div>

      {/* ====================================================== */}
      {/* HEADER SECTION */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #1e3a8a 0%, #2563eb 50%, #60a5fa 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>
            Purchase Return Management
          </h4>

          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Purchase return processing and material return management
          </div>
        </div>

        {canCreate && (
          <button
            className="btn btn-light"
            onClick={openCreateDrawer}
          >
            + Create Purchase Return
          </button>
        )}
      </div>

      {/* ====================================================== */}
      {/* KPI SUMMARY BAR */}
      {/* ====================================================== */}
      <PurchaseReturnKpiBar rows={items} />

      {/* ====================================================== */}
      {/* FILTER BAR */}
      {/* ====================================================== */}
      <PurchaseReturnFilterBar
        filters={filters}
        onChange={setFilters}
        onSearch={reload}
      />

      {/* ====================================================== */}
      {/* TABLE LIST */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">

          <table className="table table-bordered mb-0">

            <thead className="table-light">
              <tr>
                {purchaseReturnColumns.map((c) => (
                  <th key={c.key}>
                    {c.title}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>

              {loading && (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center"
                  >
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-muted"
                  >
                    No Purchase Return records found
                  </td>
                </tr>
              )}

              {!loading &&
                items.map((row) => (
                  <tr key={row.id}>

                    {purchaseReturnColumns.map((col) => (
                      <td key={col.key}>
                        {col.render
                          ? col.render(row, { navigate })
                          : row[col.key]}
                      </td>
                    ))}

                  </tr>
                ))}

            </tbody>

          </table>

        </div>
      </div>

      {/* ====================================================== */}
      {/* PAGINATION */}
      {/* ====================================================== */}
      <div className="erp-pagination">

        <span>
          Page {page} of {Math.ceil(total / pageSize)}
        </span>

        <button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          Prev
        </button>

        <button
          disabled={page * pageSize >= total}
          onClick={() => setPage(page + 1)}
        >
          Next
        </button>

      </div>

      {/* ====================================================== */}
      {/* CREATE PURCHASE RETURN DRAWER */}
      {/* ====================================================== */}
      <PurchaseReturnFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedPurchaseReturn}
        onClose={closeDrawer}
        onSaved={reload}
      />

    </div>
  );
}